import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { IoIosList } from "react-icons/io";
import { IoGridOutline } from "react-icons/io5";
import FullScreenLoader from "../../components/FullScreenLoader";
import DiamondGrid from "./DiamondGrid";
import DiamondList from "./DiamondList";
import useLocalStorageState from "../../hooks/useLocalStorageState";
import useCreateDiamondProductMutation from "../../hooks/useCreateDiamondProductMutation";

function ResultsPanel({
    naturalData,
    loading,
    isError,
    error,
    diamondcount,
    countLoading,
    onLoadMore,
    moreLoading,
}) {
    // rowSelection keyed by STOCK_ID (set via getRowId in DiamondList).
    // Survives data reloads and works across tabs.
    const [rowSelection, setRowSelection] = useState({});
    const [tab, setTab] = useState(0);
    const [isGrid, setIsGrid] = useLocalStorageState("isGrid", true);
    const createMut = useCreateDiamondProductMutation();
    // Keep loader up through navigation: createMut.isPending flips false on
    // onSuccess in the same tick as window.location.href is set, which causes
    // a flash of the unloaded page. `redirecting` stays true until unload.
    const [redirecting, setRedirecting] = useState(false);
    const viewDiamondLoader = createMut.isPending || redirecting;

    // Returning via the browser Back button restores this page from the
    // back/forward cache with `redirecting` (and any stale pending mutation)
    // frozen true — which would leave FullScreenLoader spinning forever. Clear
    // them on bfcache restore so the loader closes when the user comes back.
    useEffect(() => {
        const onPageShow = (event) => {
            if (event.persisted) {
                setRedirecting(false);
                createMut.reset();
            }
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
        // createMut.reset is referentially stable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Derive selected rows from naturalData + rowSelection.
    // Compare tab consumes this filtered list; same rowSelection drives both tabs.
    const selectedRowsData = useMemo(
        () =>
            (naturalData || []).filter(
                (d) => d?.STOCK_ID != null && rowSelection[d.STOCK_ID]
            ),
        [naturalData, rowSelection]
    );

    const handleViewDiamond = useCallback(
        (item) => {
            createMut.mutate(item, {
                onSuccess: (result) => {
                    if (result?.product_url) {
                        // Set flag FIRST so loader stays mounted through the
                        // page unload triggered by navigation.
                        setRedirecting(true);
                        window.location.href = result.product_url;
                    }
                },
            });
        },
        [createMut]
    );

    const TabHeader = (
        <div className="flex flex-wrap gap-2 justify-between items-center custom-scrollbar mb-2 md:mb-4">
            <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm md:text-base text-wahchan-primary min-w-0">
                <li
                    className={`whitespace-nowrap ${
                        tab === 0 ? "font-semibold doc-selected-tab" : "cursor-pointer"
                    }`}
                    onClick={() => setTab(0)}
                >
                    <span>
                        Showing Diamonds (
                        {countLoading ? "..." : diamondcount})
                    </span>
                </li>
                <li
                    className={`whitespace-nowrap ${
                        tab === 1 ? "font-semibold doc-selected-tab" : "cursor-pointer"
                    }`}
                    onClick={() => setTab(1)}
                >
                    <span>Comparison ({selectedRowsData.length})</span>
                </li>
            </ul>
            <button
                type="button"
                onClick={() => setIsGrid(!isGrid)}
                className="btn-outline p-1 shrink-0"
                aria-label={isGrid ? "Switch to list" : "Switch to grid"}
            >
                {isGrid ? (
                    <IoIosList className="text-xl cursor-pointer" />
                ) : (
                    <IoGridOutline className="text-xl" />
                )}
            </button>
        </div>
    );

    const emptyCompare = (
        <div className="border rounded p-3 text-center mt-2 font-bold">
            Please select diamonds to compare.
        </div>
    );

    const errorState = (
        <div className="border rounded p-4 text-center mt-2">
            <p className="font-bold text-red-600 mb-2">
                Failed to load diamonds.
            </p>
            <p className="text-sm text-gray-600">
                {error?.message || "Please try again or adjust filters."}
            </p>
        </div>
    );

    const emptyResults = (
        <div className="border rounded p-6 text-center mt-2 text-gray-600">
            No diamonds match the selected filters. Try adjusting filters or
            reset to defaults.
        </div>
    );

    let body;
    if (tab === 0) {
        if (isError && !loading) {
            body = errorState;
        } else if (!loading && naturalData.length === 0) {
            body = emptyResults;
        } else if (isGrid) {
            body = (
                <DiamondGrid
                    naturalData={naturalData}
                    loading={loading}
                    moreLoading={moreLoading}
                    from="result"
                    onScrollEnd={onLoadMore}
                    handleViewDiamond={handleViewDiamond}
                />
            );
        } else {
            body = (
                <DiamondList
                    naturalData={naturalData}
                    loading={loading}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    moreLoading={moreLoading}
                    from="result"
                    onScrollEnd={onLoadMore}
                    handleViewDiamond={handleViewDiamond}
                    viewDiamondLoader={viewDiamondLoader}
                    setTab={setTab}
                />
            );
        }
    } else {
        // tab === 1 (Comparison)
        if (selectedRowsData.length === 0) {
            body = emptyCompare;
        } else if (isGrid) {
            body = (
                <DiamondGrid
                    naturalData={selectedRowsData}
                    loading={false}
                    moreLoading={false}
                    from="compare"
                    handleViewDiamond={handleViewDiamond}
                />
            );
        } else {
            body = (
                <DiamondList
                    naturalData={selectedRowsData}
                    loading={false}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    from="compare"
                    handleViewDiamond={handleViewDiamond}
                    viewDiamondLoader={viewDiamondLoader}
                    setTab={setTab}
                />
            );
        }
    }

    return (
        <>
            <div className="my-4 px-4">
                {TabHeader}
                <div>{body}</div>
            </div>
            <FullScreenLoader loading={viewDiamondLoader} />
        </>
    );
}

export default memo(ResultsPanel);
